import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ChatService } from '@app/core/services/chat.service';
import { loadMe } from '@app/store/actions/profile.actions';
import { logger } from '@core/helpers/logger';
import { AccountService, FeaturesService, FilesService, PlatformService, SchoolsService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { getMe } from '@store/reducers/profile.reducer';
import { CSchoolParticipant, ESchoolParticipantRole, TenantEnum, User } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover } from 'onclass-ui';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-user-card-mini',
  templateUrl: './user-card-mini.component.html',
  styleUrls: ['./user-card-mini.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserCardMiniComponent extends ChangableComponent implements OnInit {
  @Input() public set user(user: Partial<User>) {
    this._user = user;
    this.prepareCard();
  }
  public get user(): Partial<User> {
    return this._user;
  }
  @Input() public set participant(participant: CSchoolParticipant) {
    this._participant = participant;
  }
  @Output() favoriteChanged = new EventEmitter<boolean>();
  @Output() onRemove = new EventEmitter<boolean>();
  public accountRoute: string[];
  public userFullName: string;
  public userLink: string;
  public userPicUrl: string;
  public countryName: string;
  public complainFormOpened = false;
  public avatar: OnUiCover;
  public alertTeacherToStudentOpened: boolean;
  public alertOwnerToTeacherOpened: boolean;

  private _user: Partial<User>;
  private _me: User;
  private _participant: CSchoolParticipant;

  constructor(
    private store: Store,
    private chatService: ChatService,
    private schoolService: SchoolsService,
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    public features: FeaturesService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  ngOnInit() {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadMe());
        },
      });
  }

  public isMe(): boolean {
    return this._me?.id === this._user?.id;
  }

  public isOwner(): boolean {
    return this._participant?.role === ESchoolParticipantRole.owner;
  }

  public isTeacher(): boolean {
    return this._participant?.role === ESchoolParticipantRole.teacher;
  }

  public isMeVersion(): boolean {
    return this.features.tenant === TenantEnum.onclass_me;
  }

  public onToggleAlertTeacherToStudent() {
    this.alertTeacherToStudentOpened = !this.alertTeacherToStudentOpened;
    this.detectChanges();
  }

  public onToggleAlertOwnerToTeacher() {
    this.alertOwnerToTeacherOpened = !this.alertOwnerToTeacherOpened;
    this.detectChanges();
  }

  public removeRole() {
    this.onRemoveParticipant();
  }

  public removeTeacher() {
    this.schoolService
      .removeTeacher(this._participant?.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.onRemove.emit(true);
          this.detectChanges();
        },
        error: (err) => {
          logger.error('UserCardMiniComponent::removeTeacher::Error', err);
          this.detectChanges();
        },
      });
  }

  private onRemoveParticipant() {
    this.schoolService
      .removeParticipant(this._participant?.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.onRemove.emit(true);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
          this.detectChanges();
        },
      });
  }

  protected prepareCard() {
    this.accountRoute = AccountService.accountRoute(this._user);
    this.userFullName = AccountService.getUserName(this._user);
    this.userLink = this.accountRoute.join('/');
    this.userPicUrl =
      FilesService.getFileBySize(this._user?.userpic)?.url || FilesService.getFileUrlBySize(this._user?.userpic_id);
    this.avatar = {
      type: 'avatar',
      img: this.userPicUrl,
      title: this.userFullName,
      link: this.accountRoute.join('/'),
      form: 'circle',
    };
  }

  public openComplainForm() {
    this.complainFormOpened = true;
    this.detectChanges();
  }

  public closeComplainForm() {
    this.complainFormOpened = false;
    this.detectChanges();
  }

  public onOpenChat(user_id: string) {
    this.chatService.openPrivateChat(user_id);
  }

  public onFavoriteChanged() {
    this.favoriteChanged.emit(true);
  }
}
